import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getEventByCode, getBoutByCode, getPoolByCode, getBoutByRefCode, getPoolBySpectateCode, getBoutBySpectateCode, lookupCode } from "../firebase/services";
import { useAuth } from "../hooks/useAuth";
import logo from "../assets/logo.png";
import AboutModal from "../components/AboutModal";

export default function Home() {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setError("");
    setLoading(true);
    const upperCode = code.trim().toUpperCase();

    try {
      // Fast path: single read from codes collection
      const lookup = await lookupCode(upperCode);
      if (lookup) {
        if (lookup.type === "event") {
          const event = await getEventByCode(upperCode);
          if (event) {
            setLoading(false);
            if (user && event.createdBy === user.uid) {
              navigate(`/event/${event.id}`);
            } else {
              navigate(`/public/${event.id}`);
            }
            return;
          }
        } else if (lookup.type === "pool") {
          const poolResult = await getPoolByCode(upperCode);
          if (poolResult) {
            setLoading(false);
            navigate(`/pool/${poolResult.eventId}/${poolResult.disciplineId}/${encodeURIComponent(poolResult.pool.name)}`);
            return;
          }
        } else if (lookup.type === "boutOfficial") {
          const bout = await getBoutByCode(upperCode);
          if (bout) {
            setLoading(false);
            navigate(`/bout/join`, { state: { bout } });
            return;
          }
        } else if (lookup.type === "boutRef") {
          const refBout = await getBoutByRefCode(upperCode);
          if (refBout) {
            setLoading(false);
            navigate(`/ref/${refBout.eventId}/${refBout.id}`);
            return;
          }
        } else if (lookup.type === "bout") {
          const bout = await getBoutByCode(upperCode);
          if (bout) {
            setLoading(false);
            navigate(`/judge/join`, { state: { bout } });
            return;
          }
        } else if (lookup.type === "poolSpectate") {
          const poolResult = await getPoolBySpectateCode(upperCode);
          if (poolResult) {
            setLoading(false);
            navigate(`/spectate/pool/${poolResult.eventId}/${poolResult.disciplineId}/${encodeURIComponent(poolResult.pool.name)}`);
            return;
          }
        } else if (lookup.type === "boutSpectate") {
          const bout = await getBoutBySpectateCode(upperCode);
          if (bout) {
            setLoading(false);
            navigate(`/spectate/${bout.eventId}/${bout.id}`);
            return;
          }
        }
      }

      // Fallback: try each type (for codes created before the codes collection)
      const event = await getEventByCode(upperCode);
      if (event) {
        setLoading(false);
        if (user && event.createdBy === user.uid) {
          navigate(`/event/${event.id}`);
        } else {
          navigate(`/public/${event.id}`);
        }
        return;
      }

      const poolResult = await getPoolByCode(upperCode);
      if (poolResult) {
        setLoading(false);
        navigate(`/pool/${poolResult.eventId}/${poolResult.disciplineId}/${encodeURIComponent(poolResult.pool.name)}`);
        return;
      }

      const refBout = await getBoutByRefCode(upperCode);
      if (refBout) {
        setLoading(false);
        navigate(`/ref/${refBout.eventId}/${refBout.id}`);
        return;
      }

      const bout = await getBoutByCode(upperCode);
      if (bout) {
        setLoading(false);
        navigate(bout.accessType === "boutOfficial" ? `/bout/join` : `/judge/join`, { state: { bout } });
        return;
      }

      const spectatePoolResult = await getPoolBySpectateCode(upperCode);
      if (spectatePoolResult) {
        setLoading(false);
        navigate(`/spectate/pool/${spectatePoolResult.eventId}/${spectatePoolResult.disciplineId}/${encodeURIComponent(spectatePoolResult.pool.name)}`);
        return;
      }

      const spectateBout = await getBoutBySpectateCode(upperCode);
      if (spectateBout) {
        setLoading(false);
        navigate(`/spectate/${spectateBout.eventId}/${spectateBout.id}`);
        return;
      }

      setError("Code not found. Check and try again.");
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Try again.");
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col min-h-screen p-6">
      {/* Top Section — Code Entry */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-center mb-8">
          <h1 className="sr-only">Joy of HEMA home</h1>
          <img src={logo} alt="" className="w-full max-w-xs mx-auto" />
          <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2">
            <div className="flex-1 h-px bg-dark-500" />
            <span className="text-gray-400 text-sm font-display font-bold tracking-[0.25em] whitespace-nowrap">
              STUDY &bull; FENCE &bull; GROW
            </span>
            <div className="flex-1 h-px bg-dark-500" />
          </div>
        </div>

        <form onSubmit={handleJoin} className="w-full max-w-xs">
          <label htmlFor="home-join-code" className="text-xs font-bold text-gray-500 tracking-widest block mb-2 font-display text-center">
            ENTER CODE
          </label>
          <input id="home-join-code" aria-invalid={Boolean(error)} aria-describedby={error ? "home-code-error" : undefined}
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase().slice(0, 6)); setError(""); }}
            placeholder="ABC123"
            maxLength={6}
            className="w-full p-4 bg-dark-800 border border-dark-500 rounded-xl text-white text-center font-display text-3xl font-extrabold tracking-[0.3em] outline-none focus:border-gray-400 uppercase mb-3"
          />
          <button
            type="submit"
            disabled={loading || code.length < 4}
            className="w-full p-3.5 bg-gradient-to-br from-green-action-light to-green-action text-white font-display text-base font-bold rounded-xl tracking-wide disabled:opacity-40 border-none cursor-pointer"
          >
            {loading ? "Looking up..." : "Join"}
          </button>
          {error && (
            <div id="home-code-error" role="alert" className="text-red-400 text-sm text-center mt-3 bg-red-900 bg-opacity-20 rounded-lg p-2">
              {error}
            </div>
          )}
        </form>

        {/* Browse public events */}
        <div className="w-full max-w-xs mt-6">
          <Link to="/events"
            className="block w-full p-3 bg-dark-700 border border-dark-500 rounded-xl text-center no-underline hover:border-gray-600 transition-colors">
            <div className="font-display text-sm font-bold text-white">Browse Events</div>
            <div className="text-gray-600 text-[10px] mt-0.5">View active & upcoming public events</div>
          </Link>
        </div>

        {/* Or — Create Event */}
        <div className="w-full max-w-xs mt-3">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-dark-500" />
            <span className="text-gray-600 text-xs">or</span>
            <div className="flex-1 h-px bg-dark-500" />
          </div>

          {user ? (
            <div className="text-center">
              <Link to="/account"
                className="block w-full p-3 bg-dark-700 border border-dark-500 rounded-xl text-center no-underline hover:border-gray-600 transition-colors mb-2">
                <div className="font-display text-sm font-bold text-white">My Profile</div>
                <div className="text-gray-600 text-[10px] mt-0.5">Manage events or edit your profile</div>
              </Link>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className="text-gray-600 text-xs">Signed in as <span className="text-gray-400">{user.displayName || user.email}</span></span>
                <button onClick={logout} className="text-gray-600 text-xs bg-transparent border-none cursor-pointer hover:text-gray-400 underline">Sign out</button>
              </div>
            </div>
          ) : (
            <Link to="/login"
              className="block w-full p-3 bg-dark-700 border border-dark-500 rounded-xl text-center no-underline hover:border-gray-600 transition-colors">
              <div className="font-display text-sm font-bold text-white">Sign in to create a new event</div>
              <div className="text-gray-600 text-[10px] mt-0.5">Login or create an account</div>
            </Link>
          )}
        </div>

        {/* Footer — Judge Tools */}
        <div className="pt-4 pb-4 border-t border-dark-500 mt-6 w-full max-w-xs flex justify-center">
          <Link to="/judge"
            className="block w-full p-3 bg-dark-700 border border-dark-500 rounded-xl text-center no-underline hover:border-gray-600 transition-colors">
            <div className="font-display text-sm font-bold text-white">Judge Tools</div>
            <div className="text-gray-600 text-[10px] mt-0.5">Standalone scoring</div>
          </Link>
        </div>

        <button
          onClick={() => setShowAbout(true)}
          className="text-gray-500 hover:text-gray-300 text-xs underline bg-transparent border-none cursor-pointer mt-4 p-0"
        >
          What is Joy of HEMA?
        </button>

        {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}
      </div>
    </div>
  );
}
